import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'catering', label: 'Catering' },
  { id: 'photography', label: 'Photography & Video' },
  { id: 'clothing', label: 'Clothing & Jewellery' },
  { id: 'decor', label: 'Decor & Venue' },
  { id: 'ceremonies', label: 'Ceremonies & Rituals' },
  { id: 'vendors', label: 'Vendors & Booking' },
  { id: 'planning', label: 'Planning & Budget' },
];

const FAQ_DATA = [
  {
    category: 'planning',
    q: 'How much does a South Asian wedding cost in Canada?',
    a: 'On average, a mid-range South Asian wedding in Canada costs between $50,000\u2013$150,000 CAD. Larger Punjabi or South Indian weddings with 300\u2013600 guests can exceed $200,000. Costs vary by city \u2013 Toronto and Vancouver tend to be the most expensive. Major cost drivers include the banquet hall, catering per head, wedding photography/videography, and bridal outfit.',
  },
  {
    category: 'planning',
    q: 'How far in advance should I book vendors for my Indian wedding in Canada?',
    a: 'Book your venue and caterer 12\u201318 months in advance, especially for peak season (May\u2013June and October\u2013November). Photographers, decorators, and mehndi artists book out 6\u201312 months ahead. DJs, dhol players, and makeup artists should be confirmed 6 months out. Pandits/priests for Hindu ceremonies should be confirmed as early as possible.',
  },
  {
    category: 'planning',
    q: 'What is the average guest count for a South Asian wedding in Canada?',
    a: 'Most South Asian weddings in Canada have 200\u2013500 guests. Punjabi and Gujarati weddings often reach 500\u2013800+. Many couples opt for smaller celebrations due to hall availability and budget, with intimate South Asian weddings in the 80\u2013150 guest range becoming more common.',
  },
  {
    category: 'planning',
    q: 'What is the best time of year for an Indian wedding in Canada?',
    a: 'The most popular seasons are spring (May\u2013June) and fall (September\u2013November), when weather is pleasant and outdoor photos are beautiful. Summer can be hot for heavy lehengas and sherwanis. Winter weddings (December\u2013February) are less common but offer lower vendor pricing and hall availability.',
  },
  {
    category: 'planning',
    q: 'How do I create a South Asian wedding budget in Canada?',
    a: 'A typical breakdown: venue/catering 40\u201350%, photography/videography 10\u201315%, decor 10\u201315%, bridal outfit and jewellery 8\u201312%, music/entertainment 5\u20138%, mehndi/makeup 3\u20135%, miscellaneous 5\u201310%. Always keep a 10\u201315% buffer for unexpected costs like overtime charges or last-minute additions.',
  },
  {
    category: 'planning',
    q: 'Do I need a wedding planner for my Indian wedding in Canada?',
    a: 'A full-service wedding planner typically costs $3,000\u2013$10,000+ CAD but can save you time, stress, and money through vendor relationships and day-of coordination. For large events with 300+ guests and multi-day functions, a planner is strongly recommended. For smaller events, a day-of coordinator or online marketplace like PlanMyShaadi can help manage vendors.',
  },
  {
    category: 'planning',
    q: 'How many days does a typical South Asian wedding in Canada last?',
    a: 'Most South Asian weddings in Canada span 2\u20134 days. A typical schedule includes a Mehndi/Sangeet night, the main wedding ceremony and reception. Larger families may also include a Roka/engagement, Haldi ceremony, and a post-wedding Milni lunch. Weekend-only weddings (Friday\u2013Sunday) are most common to accommodate guests\u2019 work schedules.',
  },
  {
    category: 'planning',
    q: 'Can I legally get married in Canada with a Hindu, Sikh, or Muslim ceremony?',
    a: 'Yes! In Canada, religious ceremonies performed by a licensed officiant are legally recognized. Your pandit, granthi (Sikh priest), or Islamic imam must be registered with the province. You also need to obtain a marriage licence from your provincial government before the ceremony and file the marriage certificate afterwards.',
  },
  {
    category: 'catering',
    q: 'How much does Indian wedding catering cost per person in Canada?',
    a: 'Indian wedding catering in Canada typically costs $60\u2013$120 per person for a buffet dinner, depending on the menu complexity and city. Full-service plated dinners can cost $120\u2013$180+/person. Prices in Toronto and Vancouver tend to be higher than in Calgary or Edmonton. Always confirm whether the price includes servers, rentals, and setup/teardown.',
  },
  {
    category: 'catering',
    q: 'Should I choose a halal or vegetarian menu for my South Asian wedding?',
    a: 'Many Indian caterers in Canada offer both halal and vegetarian menus. If your guest list is mixed (Hindu, Muslim, Sikh), offering a primarily vegetarian menu with halal chicken/lamb options covers everyone. Always discuss dietary restrictions with your caterer and ensure they clearly label food stations.',
  },
  {
    category: 'catering',
    q: 'What are popular food items served at Indian weddings in Canada?',
    a: 'Popular dishes include butter chicken, dal makhani, paneer tikka, biryani, chaat stations, samosas, pani puri, naan, rice, and raita. Dessert tables typically feature gulab jamun, rasgulla, kheer, and mithai. Many couples add a late-night poutine or pizza station for Canadian guests.',
  },
  {
    category: 'catering',
    q: 'Can I hire a caterer who does both the Mehndi and the main wedding reception?',
    a: 'Yes, most South Asian caterers in Canada offer packages for multi-day events. Mehndi night catering is usually lighter (chaat, finger foods, appetizers), while the reception is a full dinner service. Ask for a bundled quote \u2013 you can often save 10\u201315% by booking the same caterer for multiple functions.',
  },
  {
    category: 'catering',
    q: 'What is the difference between a buffet and a sit-down dinner for Indian weddings?',
    a: 'Buffets are standard for South Asian weddings in Canada as they accommodate large guest counts and allow guests to choose dishes. Sit-down plated dinners are more formal and expensive, typically chosen for smaller (under 150 guests) or fusion weddings. Many couples do a hybrid: appetizers as a buffet/chaat station, main course plated.',
  },
  {
    category: 'catering',
    q: 'Do Indian caterers in Canada provide bar service?',
    a: 'Most South Asian caterers handle food only. Bar service is typically provided by the venue or a licensed bartending company. You will need a Special Occasion Permit (SOP) if serving alcohol at a private event in venues like banquet halls. Many Indian weddings also offer a fully dry event or keep the bar separate.',
  },
  {
    category: 'catering',
    q: 'Is it okay to hire a non-Indian caterer for an Indian wedding?',
    a: 'It depends on your priorities. If authentic regional flavors (Punjabi, South Indian, Gujarati) matter to your family, an experienced Indian caterer is best. However, many couples hire a high-end Canadian or fusion caterer for the reception and a separate Indian caterer for traditional functions like Mehndi night.',
  },
  {
    category: 'photography',
    q: 'How much does Indian wedding photography cost in Canada?',
    a: 'South Asian wedding photography in Canada typically costs $3,000\u2013$10,000+ CAD for a single-day package. Multi-day coverage (Mehndi, ceremony, reception) ranges from $5,000\u2013$15,000+. Videography is often purchased as an add-on or package, adding $2,000\u2013$8,000. Cinematic highlight reels and drone footage increase costs.',
  },
  {
    category: 'photography',
    q: 'What should I look for in a South Asian wedding photographer in Canada?',
    a: 'Look for a photographer with experience shooting Indian, Pakistani, or Sri Lankan weddings \u2013 they should know ceremony sequences, understand cultural moments (jaimala, pheras, baraat), and be comfortable in low-light mandap or gurudwara settings. Review full wedding galleries, not just highlight shots, and ask specifically about their experience with your community\u2019s traditions.',
  },
  {
    category: 'photography',
    q: 'Do I need a separate photographer for the Mehndi and Sangeet?',
    a: 'Many photographers offer multi-day packages. You can hire the same photographer for all events or book a second shooter or associate photographer for smaller functions like Mehndi to save costs. Ensure whoever covers the Mehndi is experienced with candid, lively, indoor photography as lighting and energy are very different from the main wedding day.',
  },
  {
    category: 'photography',
    q: 'How long does it take to get wedding photos back in Canada?',
    a: 'Most photographers deliver an online gallery of edited photos within 6\u201312 weeks after the wedding. Rush delivery (within 2\u20134 weeks) may incur an additional fee. Cinematography/highlight reels can take 3\u20136 months. Albums typically take 3\u20136 months after photo delivery. Always confirm timelines and delivery format in your contract.',
  },
  {
    category: 'photography',
    q: 'Should I hire a South Asian wedding videographer?',
    a: 'A cinematic wedding film is highly valued in South Asian culture for sharing with family in India/Pakistan and keeping as a long-term keepsake. Budget at least $3,000\u2013$6,000 for quality videography. Look for videographers who specialize in South Asian weddings \u2013 they understand the pheras, bidaai, and baraat sequences that non-specialized videographers may miss.',
  },
  {
    category: 'clothing',
    q: 'How much does a bridal lehenga cost in Canada?',
    a: 'Bridal lehengas in Canada range from $1,500\u2013$10,000+ CAD. Designer lehengas from Indian brands like Sabyasachi or Manish Malhotra can cost $5,000\u2013$30,000+ imported. Local South Asian boutiques in Toronto\u2019s Gerrard Street or Brampton carry options from $800\u2013$5,000. Budget an additional $200\u2013$500 for alterations.',
  },
  {
    category: 'clothing',
    q: 'Where can I buy or rent a bridal lehenga in Canada?',
    a: 'Toronto (Gerrard India Bazaar, Brampton\u2019s Dixie Road corridor), Vancouver (Surrey\u2019s 128th Street), and Calgary have strong South Asian bridal boutiques. You can also order from Indian designers online and have items shipped to Canada (allow 6\u20138 weeks and budget for customs duties). Some boutiques offer rental options starting from $300\u2013$800.',
  },
  {
    category: 'clothing',
    q: 'What does the groom typically wear at an Indian wedding in Canada?',
    a: 'Grooms commonly wear a sherwani (long coat with churidar), a bandhgala/Nehru suit, or for Sikh weddings, a traditional dastar (turban) with a salwar-kurta or sherwani. Designer sherwanis cost $500\u2013$3,000+ CAD. Many grooms also coordinate with the bridal lehenga colour palette. Rental options are widely available at South Asian men\u2019s wear boutiques.',
  },
  {
    category: 'clothing',
    q: 'Where can I buy Indian wedding jewellery in Canada?',
    a: 'South Asian jewellery stores are concentrated in Brampton, Scarborough (Toronto), Surrey (Vancouver), and Calgary. Popular options include bridal sets (necklace, maang tikka, jhumkas, bangles, nath). Gold jewellery is priced by weight (ask for making charges). Fashion/kundan/Polki jewellery for bridesmaids ranges from $100\u2013$800 per set.',
  },
  {
    category: 'clothing',
    q: 'How many outfits does the bride typically need for a South Asian wedding?',
    a: 'Most South Asian brides plan 3\u20135 outfits: one for the Mehndi/Sangeet (lehenga or saree), one for the main ceremony (bridal lehenga or saree), one for the reception (often a gown or lighter lehenga), and sometimes a change-of-clothes after the ceremony. Pakistani and Muslim brides may also have a separate nikkah outfit.',
  },
  {
    category: 'clothing',
    q: 'What is the cost of a bridal saree for an Indian wedding in Canada?',
    a: 'Bridal Kanjivaram, Banarasi, or Paithani sarees range from $500\u2013$5,000+ CAD depending on fabric and craftsmanship. Designer bridal sarees can cost significantly more. Many sarees are brought from India or ordered from specialized South Asian stores in Toronto and Vancouver.',
  },
  {
    category: 'decor',
    q: 'How much does Indian wedding decoration cost in Canada?',
    a: 'Floral and mandap decoration for a mid-sized Indian wedding in Canada typically costs $8,000\u2013$25,000+. A basic mandap setup starts around $3,000\u2013$5,000. Full reception hall transformation with floral centrepieces, draping, and lighting can run $15,000\u2013$40,000+. Artificial flower decor costs significantly less than fresh flowers.',
  },
  {
    category: 'decor',
    q: 'How do I find a South Asian wedding decorator in Canada?',
    a: 'Look for decorators who specialize in South Asian weddings and have experience with mandap setups, phoolon ki chaadar (flower canopy for baraat entry), and traditional floral arrangements. Ask to see their portfolio of South Asian events specifically, as not all floral designers understand Indian wedding traditions and the scale required.',
  },
  {
    category: 'decor',
    q: 'What is a mandap and how much does it cost in Canada?',
    a: 'A mandap is the ceremonial canopy under which Hindu wedding rituals take place. It symbolizes the sacred space for the ceremony. In Canada, mandap rental and decoration costs range from $2,500\u2013$15,000+ depending on size, material (fabric vs. floral), and how elaborately it\u2019s decorated. Many decorators offer mandap packages bundled with the reception decor.',
  },
  {
    category: 'decor',
    q: 'Can I have an outdoor Indian wedding in Canada?',
    a: 'Yes, outdoor Indian weddings are popular in summer (June\u2013August). Venues like estates, vineyards, and garden halls are popular. However, always have a backup plan for rain \u2013 tent rentals ($2,000\u2013$8,000) or an indoor venue alternative are essential. Consider the heat for guests wearing heavy traditional outfits and plan the schedule around golden-hour lighting for photos.',
  },
  {
    category: 'decor',
    q: 'What is a good South Asian wedding venue capacity in Canada?',
    a: 'Most South Asian weddings need venues that accommodate 300\u2013600 guests. Toronto, Brampton, Mississauga, Surrey, Calgary, and Edmonton all have banquet halls designed for large South Asian events with appropriate kitchen facilities for Indian catering. Confirm the venue allows outside caterers if you\u2019re not using their in-house catering service.',
  },
  {
    category: 'ceremonies',
    q: 'What is the Mehndi ceremony and what happens at it?',
    a: 'The Mehndi (henna) ceremony typically takes place 1\u20132 days before the wedding. A mehndi artist applies intricate henna designs to the bride\u2019s hands and feet, and sometimes to female guests and the groom. It\u2019s usually accompanied by music, dancing, and light food. Many couples now combine the Mehndi with the Sangeet (musical evening).',
  },
  {
    category: 'ceremonies',
    q: 'What is a Sangeet and how is it different from the Mehndi?',
    a: 'The Sangeet is a musical celebration where family and friends perform choreographed dances, sing songs, and celebrate the couple. The Mehndi focuses on the henna application while the Sangeet is purely entertainment. Many Canadian South Asian weddings combine both into a single evening called the \u2018Mehndi Sangeet\u2019 to save costs and simplify scheduling.',
  },
  {
    category: 'ceremonies',
    q: 'What is the Haldi ceremony?',
    a: 'Haldi (turmeric paste) is applied to the bride and groom before the wedding day as a beautifying and auspicious ritual. It\u2019s a messy, joyful ceremony typically held at the couple\u2019s family homes the morning of or day before the wedding. Many couples skip elaborate decor for this event and keep it intimate and casual, though Instagram-worthy setups with marigolds are very popular.',
  },
  {
    category: 'ceremonies',
    q: 'What is a baraat and what should I know about organizing one in Canada?',
    a: 'The baraat is the groom\u2019s wedding procession, typically involving the groom on a horse (or car/carriage), accompanied by family and friends dancing to dhol music. In Canada, baraat processions take place in parking lots, streets with permits, or inside large venues. A horse costs $500\u2013$1,500, a dhol player $300\u2013$700, and you may need a city permit for street processions.',
  },
  {
    category: 'ceremonies',
    q: 'What rituals happen during a Hindu wedding ceremony in Canada?',
    a: 'A traditional Hindu wedding includes the Ganesh puja, Jaimala (garland exchange), Kanya Daan (giving of the bride), Saat Pheras (seven circles around the sacred fire), Sindoor ceremony, and Mangalsutra tying. The ceremony is conducted by a pandit and typically lasts 1.5\u20133 hours. Many Canadian Hindu weddings include an English explanation of rituals for non-Hindu guests.',
  },
  {
    category: 'ceremonies',
    q: 'What happens at a Sikh wedding (Anand Karaj) in Canada?',
    a: 'Anand Karaj is the Sikh wedding ceremony conducted in a Gurudwara (Sikh temple). It involves four Laavaan (circumambulations of the Guru Granth Sahib) performed by the couple while hymns are sung. The ceremony is typically 1\u20132 hours. Photography inside the Gurudwara follows specific rules \u2013 always confirm with the Gurudwara administration before booking a photographer.',
  },
  {
    category: 'ceremonies',
    q: 'What is a Nikah ceremony for Pakistani/Muslim weddings in Canada?',
    a: 'The Nikah is the Islamic marriage contract, performed by an imam in the presence of witnesses. It can be a simple signing or a larger event. The Nikah is often followed by a Walima (reception). The bride and groom do not need to be in the same room \u2013 the Nikah can happen with the bride in a separate room accepting through a representative (vakeel).',
  },
  {
    category: 'ceremonies',
    q: 'What is the Bidaai ceremony?',
    a: 'Bidaai is the farewell ceremony where the bride leaves her parents\u2019 home after the wedding. It is an emotional and deeply traditional moment captured by photographers and videographers. The bride throws rice (akshat) over her shoulder for prosperity before departing. Many Canadian South Asian families observe this tradition even if the wedding itself is held at a banquet hall.',
  },
  {
    category: 'vendors',
    q: 'Where can I find Indian wedding vendors in Canada?',
    a: 'PlanMyShaadi.ca is Canada\u2019s dedicated South Asian wedding marketplace. You can also find vendors through community Facebook groups, the South Asian community events on Eventbrite, wedding expos like the South Asian Bridal Show, and Instagram searches with location tags like #TorontoIndianWedding or #BramptonWedding.',
  },
  {
    category: 'vendors',
    q: 'How much does a mehndi artist charge for a wedding in Canada?',
    a: 'Mehndi artists in Canada charge $150\u2013$500 for the bridal application (both hands and feet, intricate design) and $25\u2013$75 per guest. Full-event packages for 30\u201350 guests at a Mehndi night range from $1,000\u2013$2,500. Rates in Toronto and Vancouver are higher. Always review their portfolio for South Asian bridal henna, not just generic designs.',
  },
  {
    category: 'vendors',
    q: 'How much does a dhol player cost for an Indian wedding in Canada?',
    a: 'A dhol player for a baraat procession in Canada typically costs $300\u2013$700 for a 1\u20132 hour performance. Multi-dhol groups (2\u20134 players) cost $600\u2013$1,500+. Peak season rates (May\u2013June, October\u2013November) tend to be higher. Book well in advance as experienced dhol players are in high demand during wedding season.',
  },
  {
    category: 'vendors',
    q: 'How much does South Asian bridal makeup cost in Canada?',
    a: 'Bridal makeup by an experienced South Asian makeup artist in Canada costs $250\u2013$700 for the bride\u2019s wedding day makeup and hair. Engagement/Mehndi night packages add $150\u2013$400. Artists who specialize in South Asian bridal looks (bold eyes, contouring for Indian skin tones, dupatta pinning) are preferred. Book 6\u20139 months in advance for peak season dates.',
  },
  {
    category: 'vendors',
    q: 'Do I need a Bollywood DJ or can I use any DJ for an Indian wedding?',
    a: 'A DJ experienced with South Asian music is strongly recommended. They should know Bollywood hits across decades, bhangra, dandiya, filmi remixes, and how to read a mixed desi crowd. A specialized South Asian DJ costs $1,000\u2013$3,500 for reception coverage. Always do a music consultation beforehand and share a do-not-play list.',
  },
  {
    category: 'vendors',
    q: 'How do I find a Hindu pandit or Sikh granthi for my wedding in Canada?',
    a: 'Pandits for Hindu weddings can be found through temple communities, South Asian wedding directories, or platforms like PlanMyShaadi. Fees range from $300\u2013$1,000+ depending on ceremony length and travel. Sikh Anand Karaj ceremonies are conducted at the Gurudwara by granthis. Always confirm the priest is registered as an officiant in your province for legal marriage.',
  },
  {
    category: 'vendors',
    q: 'Should I hire professional bhangra or Bollywood dancers for my wedding?',
    a: 'Professional bhangra and Bollywood dance troupes can add incredible energy to the baraat and reception. In Canada, group performances cost $800\u2013$3,000+ depending on the number of performers and set length. Many couples hire a mix \u2013 professional dancers for the baraat entry and self-choreographed family performances for the Sangeet.',
  },
  {
    category: 'planning',
    q: 'How do I accommodate out-of-town guests for an Indian wedding in Canada?',
    a: 'Block book a hotel room block near your venue 6\u20139 months in advance. Negotiate a group rate and share a booking link with guests. Many Indian weddings in Canada now use a wedding website to share hotel info, schedules, and transport details. Consider arranging a shuttle between the hotel and venue for guests who don\u2019t drive or for late-night departures.',
  },
  {
    category: 'planning',
    q: 'Do I need wedding insurance for my South Asian wedding in Canada?',
    a: 'Wedding insurance in Canada is affordable ($200\u2013$500 for a policy) and covers event cancellation, vendor no-shows, property damage, and liability. It\u2019s particularly worthwhile for large weddings with $50,000+ in vendor contracts. Some venues require liability insurance. Companies like Duuo or specialty wedding insurers offer policies tailored for large events.',
  },
  {
    category: 'planning',
    q: 'What permits do I need for an outdoor Indian wedding in Canada?',
    a: 'If your baraat processes on a public street, you need a municipal event permit (contact your city\u2019s events office). For outdoor tented receptions on private property, you may need a building permit for large tents and a liquor licence (SOP). Always check with your city \u2013 Toronto, Brampton, and Vancouver each have different rules for outdoor events.',
  },
  {
    category: 'planning',
    q: 'How do I manage seating for 400+ guests at an Indian wedding?',
    a: 'Use a table seating chart (not open seating) for easier service and flow. Round tables of 8\u201310 are standard. Separate VIP/family tables near the stage. Use a seating app like AllSeated or a spreadsheet to manage last-minute changes. For South Asian weddings, be prepared for guests arriving in large family groups \u2013 flexible seating near the edges helps.',
  },
  {
    category: 'planning',
    q: 'How do I write wedding invitations for a South Asian wedding in Canada?',
    a: 'South Asian wedding invitations typically include separate cards for each function (Mehndi, ceremony, reception), family names in traditional order, date/time/venue details, and RSVP information. Digital invites via WhatsApp and email are increasingly common in Canada. For formal printed invites, budget $500\u2013$2,000+ for design and printing. Include a bilingual element (Punjabi, Hindi, Urdu, Tamil) for elder guests.',
  },
  {
    category: 'planning',
    q: 'What is a wedding website and should I make one for my Indian wedding?',
    a: 'A wedding website is a simple webpage sharing your wedding schedule, venue details, hotel blocks, dress code, and RSVP form. Tools like Zola, The Knot, or Squarespace make free websites easy to create. For South Asian multi-day weddings with many out-of-town guests, a wedding website is extremely helpful for managing RSVPs and sharing event-specific details for each function.',
  },
];

const categoryLabels = {
  planning: 'Planning',
  catering: 'Catering',
  photography: 'Photography',
  clothing: 'Clothing',
  decor: 'Decor & Venue',
  ceremonies: 'Ceremonies',
  vendors: 'Vendors',
};

const FAQItem = ({ faq, index, isOpen, onToggle }) => (
  <div
    className="border-b border-maroon-100/40 last:border-b-0"
    data-testid={`faq-item-${index}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-4 py-5 px-1 text-left group"
      aria-expanded={isOpen}
      data-testid={`faq-toggle-${index}`}
    >
      <div className="flex items-start gap-3 flex-1 min-w-0">
        <span className="inline-block text-xs font-semibold uppercase tracking-wider text-gold-600 bg-gold-50 border border-gold-200/60 rounded-md px-2 py-0.5 mt-0.5 shrink-0">
          {categoryLabels[faq.category]}
        </span>
        <span className="font-medium text-maroon-900 text-base leading-snug group-hover:text-maroon-700 transition-colors">
          {faq.q}
        </span>
      </div>
      <ChevronDown
        className={`w-5 h-5 text-maroon-400 shrink-0 mt-0.5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeInOut' }}
          className="overflow-hidden"
        >
          <p className="text-gray-600 text-sm leading-relaxed pb-5 pl-1 pr-4">
            {faq.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export default function FAQPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [openIndex, setOpenIndex] = useState(null);

  const filtered = FAQ_DATA.filter((faq) => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch =
      !searchQuery ||
      faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  useEffect(() => {
    setOpenIndex(null);
  }, [activeCategory, searchQuery]);

  // JSON-LD structured data for SEO — injected via DOM to avoid dangerouslySetInnerHTML
  const jsonLdRef = useRef(null);
  useEffect(() => {
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ_DATA.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.a,
        },
      })),
    };
    let script = jsonLdRef.current;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      document.head.appendChild(script);
      jsonLdRef.current = script;
    }
    script.textContent = JSON.stringify(jsonLd);
    return () => { if (script.parentNode) script.parentNode.removeChild(script); };
  }, []);

  return (
    <div className="min-h-screen bg-cream-50">

      {/* Hero */}
      <section className="bg-maroon-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4af37'%3E%3Cpath d='M30 0l30 30-30 30L0 30z'/%3E%3C/g%3E%3C/svg%3E")`
        }} />
        <div className="max-w-4xl mx-auto px-4 py-16 md:py-24 text-center relative z-10">
          <h1
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
            data-testid="faq-page-title"
          >
            South Asian Wedding FAQs
          </h1>
          <p className="text-cream-200/80 text-base md:text-lg max-w-2xl mx-auto mb-2">
            50 questions couples and families actually search for
          </p>
          <p className="text-gold-400 text-sm font-medium">Canada</p>
        </div>
      </section>

      {/* Sticky filter bar */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-maroon-100/30 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-3">
          {/* Search */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400/40 focus:border-gold-400"
              data-testid="faq-search"
            />
          </div>
          {/* Category pills */}
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide" data-testid="faq-category-filters">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  activeCategory === cat.id
                    ? 'bg-maroon-800 text-white border-maroon-800'
                    : 'bg-white text-maroon-700 border-maroon-200 hover:border-maroon-400'
                }`}
                data-testid={`faq-cat-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ list */}
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <p className="text-sm text-gray-500 mb-6" data-testid="faq-count">
          Showing {filtered.length} of {FAQ_DATA.length} questions
        </p>
        <div className="bg-white rounded-2xl border border-maroon-100/30 shadow-sm divide-y-0 px-5 md:px-8">
          {filtered.length === 0 ? (
            <div className="py-12 text-center text-gray-400 text-sm">
              No FAQs match your search. Try a different keyword.
            </div>
          ) : (
            filtered.map((faq, idx) => (
              <FAQItem
                key={`${faq.category}-${idx}`}
                faq={faq}
                index={idx}
                isOpen={openIndex === idx}
                onToggle={() => setOpenIndex(openIndex === idx ? null : idx)}
              />
            ))
          )}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 text-sm mb-4">
            Still have questions? Let us match you with the right vendors.
          </p>
          <Link
            to="/"
            className="inline-block bg-maroon-800 hover:bg-maroon-700 text-white font-medium px-8 py-3 rounded-xl transition-colors text-sm"
            data-testid="faq-cta"
          >
            Try Shaadi Genie
          </Link>
        </div>
      </div>
    </div>
  );
}
